import Typography from '@mui/material/Typography'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import Stack from '@mui/material/Stack'
import { isAxiosError } from 'axios'
import React from 'react'

import ContainedButton from 'shared/components/Buttons/ContainedButton'
import swapUserLocationService from 'shared/services/swapUserLocation'
import { useSetActiveCartMutation } from 'shared/store/useSavedCarts'
import { useNotificationContext } from 'shared/context/Notifications'
import { getRefreshToken } from 'shared/utils/helpers/refreshToken'
import invalidateCarts from 'shared/utils/helpers/invalidateCarts'
import { useProfileQuery } from 'shared/store/useProfile'

import { FormType } from '../../../../types'

interface Props {
   handleClose: () => void
   cartUuid?: string
   notificationLocationUuid?: string
   orderType?: FormType
}

export default function OversizeOrderNotification({
   handleClose,
   notificationLocationUuid,
   cartUuid,
   orderType,
}: Props) {
   const { mutate } = useSetActiveCartMutation()
   const navigate = useNavigate()
   const { t } = useTranslation('NOTIFICATIONS')
   const { data: profile } = useProfileQuery()
   const queryClient = useQueryClient()
   const { addNotification } = useNotificationContext()

   function renderContent() {
      if (orderType === 'ORDER_CREATED') {
         if (cartUuid) {
            return <Typography variant="body1">{t('orderedWithProblems')}</Typography>
         }
         return <Typography variant="body1">{t('ordered')}</Typography>
      }
      return <Typography variant="body1">{t('notOrdered')}</Typography>
   }

   function handleLocationSwap(locationUuid: string) {
      swapUserLocationService(locationUuid)
         .then((res) => {
            if (res.status === 200) {
               getRefreshToken(queryClient)
               queryClient.invalidateQueries('userProfile')
               queryClient.invalidateQueries('userStats')
               invalidateCarts(queryClient)
            }
         })
         .then(() => {
            mutate(cartUuid!, {
               onSuccess: () => navigate('/dashboard/main-cart'),
            })
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(
                  error.response?.data?.errors[0].code || t('AUTHORIZED:header.failedLocationSwap'),
                  'error'
               )
            }
         })
   }

   function handleSetAsMain() {
      if (!notificationLocationUuid || notificationLocationUuid === profile?.locationUuid) {
         mutate(cartUuid!, {
            onSuccess: () => navigate('/dashboard/main-cart'),
         })
      } else if (notificationLocationUuid !== profile?.locationUuid) {
         if (profile?.availableLocations.find((item) => item.uuid === notificationLocationUuid)) {
            handleLocationSwap(notificationLocationUuid)
         } else {
            addNotification(t('NOTIFICATIONS:locationPermissionError'), 'error')
         }
      }
   }

   return (
      <Stack direction="column" spacing={2} sx={{ minWidth: 500, padding: 1 }}>
         {renderContent()}
         <div style={{ display: 'flex', justifyContent: 'center', gap: 3, marginTop: 20 }}>
            {cartUuid && (
               <ContainedButton variant="contained" onClick={handleSetAsMain}>
                  {t('goToCart')}
               </ContainedButton>
            )}
            <ContainedButton variant="contained" onClick={handleClose}>
               OK
            </ContainedButton>
         </div>
      </Stack>
   )
}
